import { RoomState } from '../../types/state'
import { loadUser } from '../actions/loadUser'
import { roomAction } from '../actions/workerActions'
import { RoomAction } from '../constants/enums'
import { ROOMSSTATE } from '../state'
import { getSlugAndId, getStateUser, getUser } from '../state/getState'
import { setRoomsRestored } from '../state/internal/rooms'
import { pinRoom } from '../state/state.rooms'
import { AppRouter } from '../state/state.router'
import { registerTimer, unregisterTimer } from '../state/state.timers'
import { safeObj } from '../utilities/safe'
import { getLocalStorageValue, setLocalStorage } from '../utilities/storage'
import { getSessionKey } from './history'

const ROOM_SESSION_PREFIX = 'ROOM_SESSIONS_'

export type RoomMetadata = {
    pinnedRoom?: {
        open?: boolean
        lastInteraction?: number
        index?: number
    }
}

export type Room = {
    uid: number
    metadata: RoomMetadata
}
export type RoomSessions = {
    rooms: Array<Room>
    version: 2
}

const DEFAULT_ROOM_SESSIONS = JSON.stringify({ rooms: [], version: 2 })
const thirtyMinutes = 30 * 60 * 1000

export const setupInteractionIntervals = () => {
    registerTimer(
        'pinnedRoomSession',
        () => {
            setPinnedRoomSession()
        },
        5 * 60 * 1000,
    )
    return () => {
        unregisterTimer('pinnedRoomSession')
    }
}

const quicklyLoadUsers = (rooms: Array<number>) => {
    if (rooms.length === 0) {
        return new Promise((resolve) => resolve(true))
    }
    const promises = rooms.map((id) => {
        return loadUser(id).then((exists) => {
            if (!exists) {
                forgetRoom(id)
                return
            }
            const user = getUser(id)
            if (exists && user.isModel) {
                roomAction(RoomAction.REGISTER_ROOM, id)
            } else {
                forgetRoom(id)
            }
            return exists
        })
    })
    return Promise.all(promises)
}

const migrateRooms = (sessions: any, lsKey: string): Array<Room> => {
    if (Array.isArray(sessions)) {
        // assume version 1
        // update the data in local storage and then return the room list
        const rooms = sessions.map((uid) => ({
            uid,
            metadata: {},
        }))
        setLocalStorage(
            lsKey,
            JSON.stringify({
                rooms,
                version: 2,
            }),
        )
        return rooms
    } else {
        const { version = 0 } = sessions
        if (!(version >= 2)) {
            removeOldRoomSessions()
            return []
        } else {
            // handle version 2
            const { rooms = [] } = safeObj(sessions) as RoomSessions
            return rooms
        }
    }
}

const checkAndPinRooms = (roomsToPin) => {
    const pageRoute = AppRouter.route.visiblePage
    const pageSlug = AppRouter.route.slug
    // const [, pageRoute, pageSlug] = Router.asPath.toLowerCase().split('/')
    roomsToPin.forEach((roomId) => {
        const model = getStateUser(roomId)
        // check to ensure we're not in a pm or room before restoring pin
        if (
            (pageRoute === 'message' || pageRoute === 'room') &&
            pageSlug === model.slug
        )
            return
        if (model?.isBroadcasting && !model?.banned) {
            pinRoom(roomId, { pinned: true })
        }
    })
}

/** Retrieves recently visited rooms from localStorage */
export const restoreRooms = async () => {
    if (typeof window === 'undefined') return
    if (!localStorage) return

    const ROOM_SESSIONS = getSessionKey(ROOM_SESSION_PREFIX)
    const sessions = getLocalStorageValue(ROOM_SESSIONS)
    if (!sessions) {
        setRoomsRestored(0)
        return
    }

    try {
        const roomSessions = migrateRooms(JSON.parse(sessions), ROOM_SESSIONS)
        const rooms = roomSessions
            .sort(
                (a, b) =>
                    a.metadata.pinnedRoom?.index - b.metadata.pinnedRoom?.index,
            )
            .map((room) => room.uid)
            .filter((id) => !!id)
        // check for pinned rooms
        const roomsToPin = []
        roomSessions.forEach((room) => {
            const { open, lastInteraction } = safeObj(room.metadata.pinnedRoom)
            // check to see if this is a candidate for re-opening based on previous
            //  status and last interaction time
            if (open && lastInteraction > +new Date() - thirtyMinutes) {
                // re-open this pinned room if...
                // 1) model is still online and broadcasting
                // 2) not banned or anything else
                roomsToPin.push(room.uid)
            }
        })
        if (roomsToPin.length > 0) {
            quicklyLoadUsers(roomsToPin).then(() => {
                checkAndPinRooms(roomsToPin)
            })
        }
        return quicklyLoadUsers(rooms.reverse()).then(() =>
            setRoomsRestored(rooms.length),
        )
    } catch (error) {
        console.error(error)
        setRoomsRestored(0)
        setLocalStorage(ROOM_SESSIONS, DEFAULT_ROOM_SESSIONS)
    }
}

const getRoomSessions = (ROOM_SESSIONS_KEY: string): RoomSessions => {
    if (typeof window === 'undefined') return
    if (!localStorage) return

    const roomSessions = getLocalStorageValue(ROOM_SESSIONS_KEY)
    if (!roomSessions) {
        setLocalStorage(ROOM_SESSIONS_KEY, DEFAULT_ROOM_SESSIONS)
        return
    }
    const sessions = JSON.parse(roomSessions) as RoomSessions
    const { rooms } = safeObj(sessions)
    if (!Array.isArray(rooms)) {
        setLocalStorage(ROOM_SESSIONS_KEY, DEFAULT_ROOM_SESSIONS)
        return JSON.parse(DEFAULT_ROOM_SESSIONS) as RoomSessions
    }
    return sessions
}

export const setPinnedRoomSession = (
    idOrSlug?: number | string,
    metadata?: RoomMetadata['pinnedRoom'],
) => {
    const ROOM_SESSIONS_KEY = getSessionKey(ROOM_SESSION_PREFIX)
    const sessions = getRoomSessions(ROOM_SESSIONS_KEY)
    const { rooms = [] } = safeObj(sessions)

    let sessionList = [...rooms] as Array<Room>

    if (idOrSlug) {
        const [, id] = getSlugAndId(idOrSlug)
        const oldSessionIndex = sessionList.findIndex((room) => room.uid === id)
        const oldSession = sessionList[oldSessionIndex]
        // ensure we're always updating the current one, whether it's pinned or not
        const session = {
            uid: id,
            metadata: {
                pinnedRoom: {
                    ...oldSession?.metadata?.pinnedRoom,
                    ...metadata,
                },
            },
        }
        if (oldSessionIndex >= 0) {
            sessionList[oldSessionIndex] = session
        } else {
            sessionList = [session, ...sessionList]
        }
    }

    // update the data for currently pinned rooms
    // const pinnedRooms = Array.from(ROOMSTATE.pinnedRooms)
    ROOMSSTATE.pinnedRooms.forEach((roomId, index) => {
        const oldSessionIndex = sessionList.findIndex(
            (room) => room.uid === roomId,
        )
        const roomSession = sessionList[oldSessionIndex]
        // console.log(
        //     '[setPinnedRoomSession -> pinnedRooms loop]',
        //     idOrSlug,
        //     { roomSession, roomId, oldSessionIndex },
        //     sessionList,
        // )
        if (roomSession) {
            roomSession.metadata.pinnedRoom = {
                ...roomSession.metadata.pinnedRoom,
                lastInteraction: +new Date(),
                index,
            }
        }
    })

    // Limit sessions to 20 rooms
    const newRooms = sessionList.slice(0, 20)

    setLocalStorage(
        ROOM_SESSIONS_KEY,
        JSON.stringify({ ...sessions, version: 2, rooms: newRooms }),
    )
}

export const forgetRoom = (id: number) => {
    roomAction(RoomAction.FORGET_ROOM, id)

    if (typeof window === 'undefined') return
    if (!localStorage) return

    const ROOM_SESSIONS = getSessionKey(ROOM_SESSION_PREFIX)
    const roomSessions = getLocalStorageValue(ROOM_SESSIONS)
    if (!roomSessions) {
        setRoomsRestored(0)
        setLocalStorage(ROOM_SESSIONS, DEFAULT_ROOM_SESSIONS)
        return
    }
    const sessions = JSON.parse(roomSessions) as RoomSessions
    const { rooms } = safeObj(sessions)
    if (!Array.isArray(rooms)) {
        setLocalStorage(ROOM_SESSIONS, DEFAULT_ROOM_SESSIONS)
        setRoomsRestored(sessions.rooms.length)
        return
    }
    const filteredSessions = rooms.filter((room) => room.uid !== id)
    setRoomsRestored(filteredSessions.length)

    setLocalStorage(
        ROOM_SESSIONS,
        JSON.stringify({ ...sessions, version: 2, rooms: filteredSessions }),
    )
}

export const removeOldRoomSessions = () => {
    setRoomsRestored(0)
    if (typeof window === 'undefined') return
    if (!localStorage) return
    const ROOM_SESSIONS = getSessionKey(ROOM_SESSION_PREFIX)

    localStorage.removeItem(ROOM_SESSIONS)
}

export const getActiveRooms = (roomAppState: RoomState): Array<string> => {
    return Object.keys(roomAppState).filter((room) => {
        const chat = roomAppState[room]
        return chat.isSubscribed
    })
}
